import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatChipInputEvent } from '@angular/material/chips';
import { FormControl } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { emailFormatValidator } from '@app/utilities/device-alert';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DialogService } from '@app/old-ui/dialog/dialog.service';
import { WebClient } from '@injectables';
import { DateTime } from '@lib/date-time';
import { Device } from '@models';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'device-alerts',
    templateUrl: './device-alerts.component.html',
    styleUrls: ['./device-alerts.component.scss'],
})
export class DeviceAlertsComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();

    public device: Device;
    public interval = new DateTime(new DateTime().setHours(0, 0, 0, 0));
    public toleranceSize = 0;
    public noOfRocks = 0;
    public alertEmails: string[] = [];
    public emailFormControl = new FormControl('', [emailFormatValidator]);

    public readonly separatorKeysCodes: number[] = [ENTER, COMMA];
    public readonly devices$ = this.fragQuery.devices$;
    public readonly device$ = this.fragQuery.device$;
    public readonly deviceUuid$ = this.fragQuery.deviceUuid$;
    public readonly particleSizeUnitSystem$ = this.fragQuery.particleSizeUnitSystem$;

    constructor(
        private readonly webClient: WebClient,
        private readonly fragQuery: FragQueryService,
        private readonly dialog: DialogService,
        private readonly snackBar: MatSnackBar
    ) { }

    ngOnInit(): void {
        this.device$
            .pipe(takeUntil(this.destroy$))
            .subscribe(device => this.updateDeviceSettings(device));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private updateDeviceSettings(device: Device): void {
        this.device = device;
        const deviceTolerance = device?.toleranceAlert ? JSON.parse(device.toleranceAlert) : null;

        this.alertEmails = device?.alertEmail
            ? device.alertEmail.split(',').map(e => e.trim()).filter(e => e)
            : [];

        this.interval = new DateTime(
            new DateTime().setHours(
                deviceTolerance?.duration?.hours ?? 0,
                deviceTolerance?.duration?.minutes ?? 0,
                0,
                0
            )
        );
        this.toleranceSize = deviceTolerance?.size ?? 0;
        this.noOfRocks = deviceTolerance?.number ?? 0;
    }

    public onTimeIntervalChange(interval: DateTime): void {
        this.interval = interval;
    }

    public async saveSettings(): Promise<void> {
        if (!this.device) {
            this.snackBar.open('No device selected', 'OK', {
                duration: 5000,
                panelClass: ['snackbar-success'],
            });
            return;
        }

        if (this.emailFormControl.invalid) {
            this.snackBar.open('Cannot save, invalid email format.', 'OK', {
                duration: 4000,
                panelClass: ['snackbar-success'],
            });
            return;
        }

        const toleranceAlert = {
            size: this.toleranceSize,
            duration: {
                hours: this.interval.getHours(),
                minutes: this.interval.getMinutes(),
            },
            number: this.noOfRocks,
        };

        try {
            this.device.toleranceAlert = JSON.stringify(toleranceAlert);
            this.device.alertEmail = this.alertEmails.join(',');

            await this.dialog.await(this.webClient.model.device.save(this.device));

            this.snackBar.open('Settings saved successfully', 'OK', {
                duration: 5000,
                panelClass: ['snackbar-success'],
            });

            this.fragQuery.trigger$.next();
        } catch (error) {
            console.error('Save failed:', error);
            this.snackBar.open('Failed to save settings.', 'OK', {
                duration: 3000,
                panelClass: ['snackbar-error'],
            });
        }
    }

    //add email on the enter or (,) click
    public addEmail(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();
        if (!value) return;

        // Reset the form control before validating the new input
        this.emailFormControl.setValue(value);
        this.emailFormControl.markAsDirty();

        if (this.emailFormControl.valid && !this.alertEmails.includes(value)) {
            this.alertEmails.push(value);
            event.chipInput!.clear();
            this.emailFormControl.reset(); // Reset form control after adding valid email
        } else {
            this.snackBar.open('Invalid email format', 'OK', {
                duration: 3000,
                panelClass: ['snackbar-success'],
            });
        }
    }

    public removeEmail(email: string): void {
        const index = this.alertEmails.indexOf(email);
        if (index >= 0) {
            this.alertEmails.splice(index, 1);
        }
    }

    public valid() {
        if (this.interval.getHours() === 0 && this.interval.getMinutes() === 0) return false;
        const pendingEmail = this.emailFormControl.value?.trim();
        if (pendingEmail) {
            if (!this.emailFormControl.valid) {
                return false;
            }
        } else if (this.alertEmails.length === 0) {
            return false;
        }

        return true;
    }
}